import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as E from 'linq';
import { recursive as mergeRecursive } from 'merge';
import { concat, NEVER } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CompanyFacetDto } from 'src/portal/search-api/models/company-facet-dto';
import { CompanyPageRequestDto } from 'src/portal/search-api/models/company-page-request-dto';
import { CompanyPageResponseDto } from 'src/portal/search-api/models/company-page-response-dto';
import { ContactFacetDto } from 'src/portal/search-api/models/contact-facet-dto';
import { ContactPageRequestDto } from 'src/portal/search-api/models/contact-page-request-dto';
import { ContactPageResponseDto } from 'src/portal/search-api/models/contact-page-response-dto';
import { CompanyService } from 'src/portal/search-api/services/company.service';
import { ContactService } from 'src/portal/search-api/services/contact.service';
import { GlobalSearchResultTypeEnum } from '../../portal/search-api/models/global-search-result-type-enum';
import { IStore } from '../store';
import * as SearchActions from './search.actions';
import { selectSearchState } from './search.selectors';
import {
  CompanySearch,
  ContactSearch,
  FilterItem,
  SearchFilter,
} from './search.state';

@Injectable()
export class SearchEffects {
  activeTabChange$;
  initializeAction$;
  companyInitializeAction$;
  contactInitializeAction$;
  companySearchOptionsChangedAction$;
  contactSearchOptionsChangedAction$;
  contactFilterToggledAction$;
  clearSearchAction$;
  searchAction$;
  companyPagerChangedAction$;
  contactPagerChangedAction$;
  companySortingChangedAction$;
  contactSortingChangedAction$;
  companyAdvanceFilterChangedAction$;
  contactFilterChangedAction$;
  companyFilterChangedAction$;
  companyFilterToggledAction$;
  companySearchAndFilterChangedAction$;
  ClearAllAction$;
  constructor(
    private store: Store<IStore>,
    private actions: Actions,
    private contactService: ContactService,
    private companyService: CompanyService
  ) {
    this.activeTabChange$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ActiveTabChange),
        switchMap((action) => [
          SearchActions.MergeStateAction({
            newState: { activeTab: action.activeTab },
          }),
          // SearchActions.InitializeAction(),
        ])
      )
    );

    this.initializeAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.InitializeAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([, searchState]) => {
          console.log('initialize action - contact');
          /*if (searchState.activeTab == "Company"
            && !searchState.companySearch.initialized) {
            //   return this.initCompanies(searchState.companySearch);
            }
            if (searchState.activeTab == "Contact"
            && !searchState.contactSearch.initialized
               ) {
           //   return this.initContacts(searchState.contactSearch);
            }
*/
          return NEVER;
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companyInitializeAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.InitializeAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([, searchState]) => {
          if (!searchState.companySearch.initialized) {
            return this.initCompanies(searchState.companySearch);
          }
          return NEVER;
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.contactInitializeAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.InitializeAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([, searchState]) => {
          if (!searchState.contactSearch.initialized) {
            return this.initContacts(searchState.contactSearch);
          }
          return NEVER;
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companySearchOptionsChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.CompanySearchOptionsChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const companySearch = {
            ...searchState.companySearch,
            searchOptions: action.searchOptions,
          };

          return concat(
            of({ companySearch: { updatingFilters: true } }),
            this.initCompanies(companySearch),
            of({ companySearch: { updatingFilters: false } })
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.contactSearchOptionsChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ContactSearchOptionsChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const contactSearch = {
            ...searchState.contactSearch,
            searchOptions: action.searchOptions,
          };

          return concat(
            of({ contactSearch: { updatingFilters: true } }),
            this.initContacts(contactSearch),
            of({ contactSearch: { updatingFilters: false } })
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.contactFilterToggledAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ContactFilterToggledAction),
        withLatestFrom(this.store.select(selectSearchState)),
        map(([action, searchState]) => {
          const currentFilter =
            searchState.contactSearch.filters[action.filterName];

          const filterClone = mergeRecursive(true, currentFilter);
          filterClone.collapsed = !filterClone.collapsed;

          return {
            contactSearch: {
              filters: { [action.filterName]: filterClone },
            },
          };
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.clearSearchAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ClearSearchAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => [
          SearchActions.MergeStateAction({
            newState: {
              companySearch: {
                initialized:
                  action.searchTerm == searchState.companySearch.searchTerm,
                searchTerm: action.searchTerm,
              },
              contactSearch: {
                initialized:
                  action.searchTerm == searchState.contactSearch.searchTerm,
                searchTerm: action.searchTerm,
              },
            },
          }),
        ])
      )
    );

    this.searchAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.SearchAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => [
          SearchActions.MergeStateAction({
            newState: {
              companySearch: {
                initialized:
                  action.searchTerm == searchState.companySearch.searchTerm,
                searchTerm: action.searchTerm,
              },
              contactSearch: {
                initialized:
                  action.searchTerm == searchState.contactSearch.searchTerm,
                searchTerm: action.searchTerm,
              },
            },
          }),
          SearchActions.InitializeAction(),
        ])
      )
    );

    this.contactSortingChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ContactSortingChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const sorting = searchState.contactSearch.sorting;

          const asc =
            action.sortField == sorting.field ? !sorting.asc : sorting.asc;

          const contactSearch = {
            ...searchState.contactSearch,
            sorting: { field: action.sortField, asc },
          };

          return concat(
            of({ contactSearch }),
            this.findContacts(contactSearch).pipe(
              map((i) => this.removeContactFilters(i))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.contactFilterChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ContactFilterChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const filterName = action.appliedFilter.filterName;
          const filterItems = action.appliedFilter[filterName];
          const filterCollapsed =
            searchState.contactSearch.filters[filterName].collapsed;

          const anyFiltersChecked = E.from(filterItems).any((c) => c.checked);
          let filteringPath = E.from(searchState.contactSearch.filteringPath);
          const alreadySelected = filteringPath.contains(filterName);

          if (anyFiltersChecked) {
            if (!alreadySelected) {
              filteringPath = filteringPath.concat([filterName]);
            }
          } else {
            filteringPath = filteringPath.except([filterName]);
          }

          const contactSearch = {
            ...searchState.contactSearch,
            loading: true,
            filters: {
              ...searchState.contactSearch.filters,
              [filterName]: { items: filterItems, collapsed: filterCollapsed },
              filterType: GlobalSearchResultTypeEnum.CONTACT,
            },
            filteringPath: filteringPath.toArray(),
            pager: {
              ...searchState.contactSearch.pager,
              pageNumber: 1,
            },
          };
          console.log('findContacts - contactfilteractionchange');

          return concat(
            of({ contactSearch }),
            this.findContacts(contactSearch).pipe(
              map((response) =>
                this.mergeContactFilters(
                  response,
                  contactSearch,
                  action.appliedFilter.filterName
                )
              ),
              catchError((error) => of({ contactSearch: { loading: false } }))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.contactPagerChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ContactPagerChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const contactSearch = {
            ...searchState.contactSearch,
            pager: {
              ...searchState.contactSearch.pager,
              pageSize: action.pageSize,
              pageNumber: action.pageNumber,
            },
          };

          return concat(
            of({ contactSearch }),
            this.findContacts(contactSearch).pipe(
              map((i) => this.removeContactFilters(i))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companyPagerChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.CompanyPagerChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const companySearch = {
            ...searchState.companySearch,
            pager: {
              ...searchState.companySearch.pager,
              pageSize: action.pageSize,
              pageNumber: action.pageNumber,
            },
          };

          return concat(
            of({ companySearch }),
            this.findCompanies(companySearch).pipe(
              map((i) => this.removeCompanyFilters(i))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companyFilterChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.CompanyFilterChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const filterName = action.appliedFilter.filterName;
          const filterItems = action.appliedFilter[filterName];
          const filterCollapsed =
            searchState.companySearch.filters[filterName].collapsed;

          const anyFiltersChecked = E.from(filterItems).any((c) => c.checked);
          let filteringPath = E.from(searchState.companySearch.filteringPath);
          const alreadySelected = filteringPath.contains(filterName);

          if (anyFiltersChecked) {
            if (!alreadySelected) {
              filteringPath = filteringPath.concat([filterName]);
            }
          } else {
            filteringPath = filteringPath.except([filterName]);
          }

          const companySearch = {
            ...searchState.companySearch,
            loading: true,
            filters: {
              ...searchState.companySearch.filters,
              [filterName]: { items: filterItems, collapsed: filterCollapsed },
              filterType: GlobalSearchResultTypeEnum.COMPANY,
            },
            filteringPath: filteringPath.toArray(),
            pager: {
              ...searchState.companySearch.pager,
              pageNumber: 1,
            },
          };

          return concat(
            of({ companySearch }),
            this.findCompanies(companySearch).pipe(
              map((response) =>
                this.mergeCompanyFilters(
                  response,
                  companySearch,
                  action.appliedFilter.filterName
                )
              ),
              catchError((error) => of({ companySearch: { loading: false } }))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companyFilterToggledAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.CompanyFilterToggledAction),
        withLatestFrom(this.store.select(selectSearchState)),
        map(([action, searchState]) => {
          const currentFilter =
            searchState.companySearch.filters[action.filterName];

          const filterClone = mergeRecursive(true, currentFilter);
          filterClone.collapsed = !filterClone.collapsed;

          return {
            companySearch: {
              filters: { [action.filterName]: filterClone },
            },
          };
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companySortingChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.CompanySortingChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          const sorting = searchState.companySearch.sorting;

          const asc =
            action.sortField == sorting.field ? !sorting.asc : sorting.asc;

          const companySearch = {
            ...searchState.companySearch,
            sorting: { field: action.sortField, asc },
          };

          return concat(
            of({ companySearch }),
            this.findCompanies(companySearch).pipe(
              map((i) => this.removeCompanyFilters(i))
            )
          );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.ClearAllAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.ClearAllAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([, searchState]) => {
          console.log(searchState);
          if (searchState.activeTab == 'Company')
            return this.initCompanies(searchState.companySearch);
          if (searchState.activeTab == 'Contact')
            return this.initContacts(searchState.contactSearch);

          return NEVER;
        }),
        tap((data) => console.log(data)),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );

    this.companySearchAndFilterChangedAction$ = createEffect(() =>
      this.actions.pipe(
        ofType(SearchActions.companySearchAndFilterChangedAction),
        withLatestFrom(this.store.select(selectSearchState)),
        switchMap(([action, searchState]) => {
          let filterOption = {};
          let filteringPath = E.from([]);
          action.appliedFilter.forEach((filter) => {
            const filterName = filter.filterName;
            const filterItems = filter[filterName];

            const anyFiltersChecked = E.from(filterItems).any((c) => c.checked);

            const alreadySelected = filteringPath.contains(filterName);

            if (anyFiltersChecked) {
              if (!alreadySelected) {
                filteringPath = filteringPath.concat([filterName]);
              }
            } else {
              filteringPath = filteringPath.except([filterName]);
            }

            filterOption = {
              ...filterOption,
              [filterName]: { items: filterItems, collapsed: false },
            };
          });

          let contactSearch;
          let companySearch;

          console.log(searchState.activeTab);

          if (searchState.activeTab == 'Contact') {
            let filters = JSON.parse(
              JSON.stringify(searchState.contactSearch.filters)
            );

            [
              'companyName',
              'department',
              'employeeRange',
              'industry',
              'level',
              'revenueRange',
              'title',
              'jobRole',
              'country',
              'location',
              'name',
              'website',
            ].forEach((filter) => {
              filters[filter].items = filters[filter].items.map((item, i) =>
                Object.assign({}, item, {
                  checked: false,
                  caption: item.caption,
                })
              );
            });
            contactSearch = {
              ...searchState.contactSearch,
              searchOptions: action.searchOptions,
              loading: true,
              filters: {
                ...filters,
                ...filterOption,
                //...action.location,
                filterType: GlobalSearchResultTypeEnum.CONTACT,
              },
              filteringPath: filteringPath.toArray(),
              pager: {
                ...searchState.contactSearch.pager,
                pageNumber: 1,
              },
            };
          } else {
            let filters = JSON.parse(
              JSON.stringify(searchState.companySearch.filters)
            );

            [
              'industry',
              'employeeRange',
              'ownership',
              'revenueRange',
              'location',
              'companyName',
              'country',
              'website',
            ].forEach((filter) => {
              filters[filter].items = filters[filter].items.map((item, i) =>
                Object.assign({}, item, {
                  checked: false,
                  caption: item.caption,
                })
              );
            });
            companySearch = {
              ...searchState.companySearch,
              searchOptions: action.searchOptions,
              loading: true,
              filters: {
                ...filters,
                //  ...searchState.companySearch.filters,
                ...filterOption,
                //  ...action.location,
                filterType: GlobalSearchResultTypeEnum.COMPANY,
              },
              filteringPath: filteringPath.toArray(),
              pager: {
                ...searchState.companySearch.pager,
                pageNumber: 1,
              },
            };
          }

          return searchState.activeTab == 'Contact'
            ? concat(
                of({ contactSearch: { updatingFilters: true } }),
                //this.initContacts(contactSearch),
                of({ contactSearch: { updatingFilters: false } }),
                of({ contactSearch }),

                this.findContacts(contactSearch).pipe(
                  map((response) => {
                    return {
                      contactSearch: {
                        loading: false,
                        rows: response.content,
                        pager: {
                          totalElements: response.totalElements,
                          totalPages: response.totalPages,
                        },
                      },
                    };
                  }),
                  catchError((error) =>
                    of({ contactSearch: { loading: false } })
                  )
                )
              )
            : concat(
                of({ companySearch: { updatingFilters: true } }),
                //this.initCompanies(companySearch),
                of({ companySearch: { updatingFilters: false } }),
                of({ companySearch }),
                this.findCompanies(companySearch).pipe(
                  map((response) => {
                    return {
                      companySearch: {
                        loading: false,
                        rows: response.content,
                        pager: {
                          totalElements: response.totalElements,
                          totalPages: response.totalPages,
                        },
                      },
                    };
                  }),
                  catchError((error) =>
                    of({ companySearch: { loading: false } })
                  )
                )
              );
        }),
        map((i) => SearchActions.MergeStateAction({ newState: i }))
      )
    );
  }

  private convertToStrings = (items: FilterItem[]): string[] => {
    const result = E.from(items)
      .where((i) => i.checked)
      .select((i) => i.caption)
      .toArray();

    return result.length ? result : null;
  };

  private findContacts(contactSearch: ContactSearch) {
    console.log('findContacts - fun');

    const body: ContactPageRequestDto = {
      pageNumber: contactSearch.pager.pageNumber - 1,
      pageSize: contactSearch.pager.pageSize,
      sort: [
        {
          asc: contactSearch.sorting.asc,
          field: contactSearch.sorting.field,
        },
      ],
      filters: {
        search: contactSearch.searchTerm,
        employeeRange: this.convertToStrings(
          contactSearch.filters.employeeRange.items
        ),
        industry: this.convertToStrings(contactSearch.filters.industry.items),
        revenueRange: this.convertToStrings(
          contactSearch.filters.revenueRange.items
        ),
        department: this.convertToStrings(
          contactSearch.filters.department.items
        ),
        jobRole: this.convertToStrings(contactSearch.filters.jobRole.items),
        level: this.convertToStrings(contactSearch.filters.level.items),
        title: this.convertToStrings(contactSearch.filters.title.items),
        companyName: this.convertToStrings(
          contactSearch.filters.companyName.items
        ),
        name: this.convertToStrings(contactSearch.filters.name.items),
        location: this.convertToStrings(contactSearch.filters.location.items),
        country: this.convertToStrings(contactSearch.filters.country.items),
        showInactive: contactSearch.searchOptions.showInactiveCompanies,
        showOnlyDirectDials: contactSearch.searchOptions.showOnlyDirectDials,
        hideOwned: contactSearch.searchOptions.hideMyOwnedCompanies,
        filterType: GlobalSearchResultTypeEnum.CONTACT,
        website: this.convertToStrings(contactSearch.filters.website.items),
      },
    };

    return this.contactService.findContacts({ body });
  }

  private initContacts(contactSearch: ContactSearch) {
    const body: ContactPageRequestDto = {
      pageNumber: contactSearch.pager.pageNumber - 1,
      pageSize: contactSearch.pager.pageSize,
      filters: {
        search: contactSearch.searchTerm,
        showInactive: contactSearch.searchOptions.showInactiveCompanies,
        showOnlyDirectDials: contactSearch.searchOptions.showOnlyDirectDials,
        hideOwned: contactSearch.searchOptions.hideMyOwnedCompanies,
        filterType: GlobalSearchResultTypeEnum.CONTACT,
      },
    };
    //console.log("findContacts - initcontact");
    return this.contactService.findContacts({ body }).pipe(
      map((response) => ({
        contactSearch: {
          initialized: true,
          searchTerm: contactSearch.searchTerm,
          filters: {
            employeeRange: this.converToCheckableFixed('EMPLOYEES'),
            industry: this.converToCheckableFixed('INDUSTRY'),
            department: this.converToCheckableFixed('DEPARTMENT'),
            jobRole: this.converToCheckableFixed('SUBDEPARTMENT'),
            revenueRange: this.converToCheckableFixed('REVENUE'),
            level: this.converToCheckableFixed('LEVEL'),
            title: this.converToCheckable(response.facets?.title),
            companyName: this.converToCheckableFixed('COMPANY'),
            name: this.converToCheckableFixed('NAME'),
            location: this.converToCheckableFixed('LOCATION'),
            country: this.converToCheckableFixed('COUNTRY'),
            website: this.converToCheckableFixed('WEBSITES'),
            filterType: GlobalSearchResultTypeEnum.CONTACT,
          },
          searchOptions: {
            showInactiveCompanies:
              contactSearch.searchOptions.showInactiveCompanies,
            showOnlyDirectDials:
              contactSearch.searchOptions.showOnlyDirectDials,
            hideMyOwnedCompanies:
              contactSearch.searchOptions.hideMyOwnedCompanies,
          },
          pager: {
            totalElements: response.totalElements,
            totalPages: response.totalPages,
          },
          rows: response.content,
        },
      }))
    );
  }

  private initCompanies(companySearch: CompanySearch) {
    const body: CompanyPageRequestDto = {
      pageNumber: companySearch.pager.pageNumber - 1,
      pageSize: companySearch.pager.pageSize,
      filters: {
        search: companySearch.searchTerm,
        showInactive: companySearch.searchOptions.showInactiveCompanies,
        showOnlyDirectDials: companySearch.searchOptions.showOnlyDirectDials,
        hideOwned: companySearch.searchOptions.hideMyOwnedCompanies,
        filterType: GlobalSearchResultTypeEnum.COMPANY,
      },
    };

    return this.companyService.findCompanies({ body }).pipe(
      tap((data) => console.log(data)),
      map((response) => ({
        companySearch: {
          initialized: true,
          searchTerm: companySearch.searchTerm,
          filters: {
            employeeRange: this.converToCheckableFixed('EMPLOYEES'),
            companyName: this.converToCheckableFixed('COMPANY'),
            industry: this.converToCheckableFixed('INDUSTRY'),
            ownership: this.converToCheckable(response.facets?.ownership),
            revenueRange: this.converToCheckableFixed('REVENUE'),
            location: this.converToCheckableFixed('LOCATION'),
            country: this.converToCheckableFixed('COUNTRY'),
            website: this.converToCheckableFixed('WEBSITES'),
            filterType: GlobalSearchResultTypeEnum.COMPANY,
          },
          searchOptions: {
            showInactiveCompanies:
              companySearch.searchOptions.showInactiveCompanies,
            showOnlyDirectDials:
              companySearch.searchOptions.showOnlyDirectDials,
            hideMyOwnedCompanies:
              companySearch.searchOptions.hideMyOwnedCompanies,
          },
          pager: {
            totalElements: response.totalElements,
            totalPages: response.totalPages,
          },
          rows: response.content,
        },
      }))
    );
  }

  private converToCheckable(strings: string[]) {
    const items = E.from(strings)
      .select((i) => ({ checked: false, caption: i }))
      .toArray();
    return { items, collapsed: true };
  }

  /*
    TODO: FIX THIS FUNCTION. TEMPORARY SOLUTION TO LOAD STATIC FACETS/FILTERS.
     */

  private converToCheckableFixed(facetType: string) {
    let strings: string[];
    if (facetType == 'DEPARTMENT') {
      strings = [
        'sales',
        'engineering',
        'marketing',
        'product management',
        'operations',
        'information technology',
        'customer success',
        'finance',
        'product marketing',
        'general management',
        'human resources',
        'other - sales',
        'business',
        'business development',
        'marketing operations',
        'legal',
        'customer success other',
        'corporate counsels',
        'enterprise architecture',
        'enterprise sales',
        'consulting',
        'corporate finance',
        'customer marketing',
        'gtm',
        'other sales',
        'revenue operations and strategy',
        'accounting',
        'administrative',
        'business operations',
        'cloud network',
        'cloud solutions',
        'corporate and business development',
        'customer solutions architecture',
        'field marketing',
        'growth',
        'information security',
        'people',
        'regional sales',
        'revenue',
        'sales other',
        'strategic finance',
        'talent acquisition',
        'technology',
      ];
    } else if (facetType == 'LEVEL') {
      strings = [
        'c level',
        'vice president',
        'director',
        'manager',
        'senior',
        'staff',
      ];
    } else if (facetType == 'EMPLOYEES') {
      strings = [
        'XLarge (5001+ Employees)',
        'Large (1001-5000 Employees)',
        'Medium-Large (501-1000 Employees)',
        'Medium (101-500 Employees)',
        'Small (21-100 Employees)',
        'Micro (1-20 Employees)',
      ];
    } else if (facetType == 'REVENUE') {
      strings = [
        '$1+ Billion',
        '$100 - 999.99 Million',
        '$50 - 99.99 Million',
        '$10 - 49.99 Million',
        '$1 - 9.99 Million',
        'Less than $1 Million',
      ];
    } else if (facetType == 'SUBDEPARTMENT') {
      strings = [
        'sales',
        'engineering',
        'marketing',
        'product management',
        'operations',
        'information technology',
        'customer success',
        'finance',
        'product marketing',
        'general management',
        'human resources',
        'other - sales',
        'business',
        'business development',
        'marketing operations',
        'legal',
        'customer success other',
        'corporate counsels',
        'enterprise architecture',
        'enterprise sales',
        'consulting',
        'corporate finance',
        'customer marketing',
        'gtm',
        'other sales',
        'revenue operations and strategy',
        'accounting',
        'administrative',
        'business operations',
        'cloud network',
        'cloud solutions',
        'corporate and business development',
        'customer solutions architecture',
        'field marketing',
        'growth',
        'information security',
        'people',
        'regional sales',
        'revenue',
        'sales other',
        'strategic finance',
        'talent acquisition',
        'technology',
      ];
    } else if (facetType == 'INDUSTRY') {
      strings = [
        'advertising',
        'analytics',
        'application software',
        'artificial intelligence',
        'blockchain',
        'broadcasting',
        'business information',
        'business intelligence',
        'business support',
        'cloud computing',
        'collaboration software',
        'computers',
        'customer relationship',
        'cybersecurity',
        'data processing',
        'database management',
        'developer community',
        'digital marketing',
        'digital media',
        'e-learning',
        'educational software',
        'email services',
        'employee training',
        'employment agencies',
        'enterprise resource',
        'enterprise software',
        'event management',
        'facilities management',
        'fintech',
        'fintech analytics',
        'health care',
        'hr software',
        'identity management',
        'internet software',
        'iot',
        'it consulting',
        'it services',
        'it support',
        'legal services',
        'life',
        'management consulting',
        'marketing services',
        'messaging',
        'messaging social',
        'networking systems',
        'online marketplace',
        'online recruitment',
        'paas',
        'payroll',
        'publishing',
        'regulatory compliance',
        'retail',
        'saas',
        'saas analytics',
        'saas cloud',
        'saas digital',
        'saas hr',
        'saas iot',
        'saas open',
        'saas sales',
        'sales enablement',
        'scheduling software',
        'search engine',
        'security software',
        'social media',
        'software',
        'software asset',
        'software testing',
        'storage technology',
        'supply chain',
        'systems software',
        'telecommunication services',
        'telemedicine',
        'travel agency',
        'ui/ux design',
        'web development',
        'web hosting',
      ];
    } else if (facetType == 'COUNTRY') {
      strings = [
        'United States',
        'Canada',
        'India',
        'Brazil',
        'Australia',
        'Mexico',
        'United Kingdom',
        'South Africa',
      ];
    } else if (facetType == 'COMPANY') {
      strings = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']; //internal names, caption gets replaced when companny gets selected
    } else if (facetType == 'LOCATION') {
      strings = [
        'L0',
        'L1',
        'L2',
        'L3',
        'L4',
        'L5',
        'L6',
        'L7',
        'L8',
        'L9',
        'L10',
      ]; //internal names, caption gets replaced when location gets selected
    } else if (facetType == 'NAME') {
      strings = [
        'P0',
        'P1',
        'P2',
        'P3',
        'P4',
        'P5',
        'P6',
        'P7',
        'P8',
        'P9',
        'P10',
      ]; //internal names, caption gets replaced when person gets selected
    } else if (facetType == 'WEBSITES') {
      strings = [];
    }

    const items = E.from(strings)
      .select((i) => ({ checked: false, caption: i }))
      .toArray();
    return { items, collapsed: true };
  }

  private mergeCompanyFilters(
    response: CompanyPageResponseDto,
    companySearch: CompanySearch,
    filterName: string
  ) {
    const heap = E.from(response.facets)
      .select((i) => i.key as keyof CompanyFacetDto)
      .except(companySearch.filteringPath)
      .toArray();

    const faceNames = E.from(companySearch.filteringPath)
      .skipWhile((i) => i != filterName)
      .skip(1)
      .concat(heap);

    const filters = faceNames.toObject(
      (i) => i,
      (i) => {
        const searchFilter: SearchFilter = companySearch.filters[i];

        const items = E.from(searchFilter.items)
          .groupJoin(
            E.from(response.facets[i]).cast<string>(),
            (l) => l.caption,
            (r) => r,
            (left, right) => ({
              checked: left.checked,
              caption: left.caption,
              disabled: right.isEmpty(),
            })
          )
          .toArray();

        return { items, collapsed: searchFilter.collapsed };
      }
    );

    return {
      companySearch: {
        loading: false,
        rows: response.content,
        filters,
        pager: {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
        },
      },
    };
  }

  private mergeContactFilters(
    response: ContactPageResponseDto,
    contactSearch: ContactSearch,
    filterName: string
  ) {
    const heap = E.from(response.facets)
      .select((i) => i.key as keyof ContactFacetDto)
      .except(contactSearch.filteringPath)
      .toArray();

    const faceNames = E.from(contactSearch.filteringPath)
      .skipWhile((i) => i != filterName)
      .skip(1)
      .concat(heap);

    const filters = faceNames.toObject(
      (i) => i,
      (i) => {
        const searchFilter: SearchFilter = contactSearch.filters[i];

        const items = E.from(searchFilter.items)
          .groupJoin(
            E.from(response.facets[i]).cast<string>(),
            (l) => l.caption,
            (r) => r,
            (left, right) => ({
              checked: left.checked,
              caption: left.caption,
              disabled: right.isEmpty(),
            })
          )
          .toArray();

        return { items, collapsed: searchFilter.collapsed };
      }
    );

    return {
      contactSearch: {
        loading: false,
        rows: response.content,
        filters,
        pager: {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
        },
      },
    };
  }

  private removeCompanyFilters(response: CompanyPageResponseDto) {
    return {
      companySearch: {
        rows: response.content,
        pager: {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
        },
      },
    };
  }

  private removeContactFilters(response: ContactPageResponseDto) {
    return {
      contactSearch: {
        rows: response.content,
        pager: {
          totalElements: response.totalElements,
          totalPages: response.totalPages,
        },
      },
    };
  }

  private findCompanies(companySearch: CompanySearch) {
    const body: CompanyPageRequestDto = {
      pageNumber: companySearch.pager.pageNumber - 1,
      pageSize: companySearch.pager.pageSize,
      sort: [
        {
          asc: companySearch.sorting.asc,
          field: companySearch.sorting.field,
        },
      ],
      filters: {
        search: companySearch.searchTerm,
        employeeRange: this.convertToStrings(
          companySearch.filters.employeeRange.items
        ),
        companyName: this.convertToStrings(
          companySearch.filters.companyName.items
        ),
        industry: this.convertToStrings(companySearch.filters.industry.items),
        ownership: this.convertToStrings(companySearch.filters.ownership.items),
        revenueRange: this.convertToStrings(
          companySearch.filters.revenueRange.items
        ),
        location: this.convertToStrings(companySearch.filters.location.items),
        country: this.convertToStrings(companySearch.filters.country.items),
        showInactive: companySearch.searchOptions.showInactiveCompanies,
        showOnlyDirectDials: companySearch.searchOptions.showOnlyDirectDials,
        hideOwned: companySearch.searchOptions.hideMyOwnedCompanies,
        filterType: GlobalSearchResultTypeEnum.COMPANY,
        website: this.convertToStrings(companySearch.filters.website.items),
      },
    };

    return this.companyService.findCompanies({ body });
  }
}
